/**
 * Challenge du samedi : paliers de ventes → bonus par vendeur ayant vendu.
 * - 3 ventes aujourd'hui = 10 € de bonus pour chaque vendeur ayant vendu
 * - 6 ventes aujourd'hui = 20 € de bonus pour chaque vendeur ayant vendu
 */

export const SATURDAY_CHALLENGE_DATE = "2026-07-25";

export const isSaturdayChallengeEnabled = () => {
  const today = new Date();
  const [year, month, day] = SATURDAY_CHALLENGE_DATE.split("-").map(Number);
  return today.getFullYear() === year && today.getMonth() === month - 1 && today.getDate() === day;
};

export const saturdayChallengeTiers = [
  { tier: "1", salesThreshold: 3, bonusPerSeller: 10 },
  { tier: "2", salesThreshold: 6, bonusPerSeller: 20 },
];

const sortTiersBySalesThreshold = (tiers, direction = "desc") => {
  const sorted = [...(tiers || [])];
  return sorted.sort((a, b) => {
    if (direction === "asc") {
      return a.salesThreshold - b.salesThreshold;
    }
    return b.salesThreshold - a.salesThreshold;
  });
};

/**
 * Retourne le palier atteint pour un nombre de ventes donné (le plus haut palier dépassé).
 * @param {number} salesCount - Nombre de ventes du magasin pour la journée
 * @param {Array} tiers - Paliers (défaut: saturdayChallengeTiers)
 * @returns {Object|null} Le palier atteint ou null
 */
export const getReachedTier = (salesCount, tiers = saturdayChallengeTiers) => {
  if (!Number.isFinite(salesCount) || salesCount < 0) return null;
  return sortTiersBySalesThreshold(tiers, "desc").find((tier) => salesCount >= tier.salesThreshold) || null;
};

/**
 * Retourne le prochain palier à atteindre.
 * @param {number} salesCount - Nombre de ventes du magasin pour la journée
 * @param {Array} tiers - Paliers (défaut: saturdayChallengeTiers)
 * @returns {Object|null} Le prochain palier ou null si tous sont atteints
 */
export const getNextTier = (salesCount, tiers = saturdayChallengeTiers) => {
  if (!Number.isFinite(salesCount) || salesCount < 0) return null;
  return sortTiersBySalesThreshold(tiers, "asc").find((tier) => salesCount < tier.salesThreshold) || null;
};

/**
 * Montant du bonus (en €) versé à chaque vendeur ayant vendu, pour un nombre de ventes donné.
 * Ex : 3 ventes → 10 €/vendeur, 6 ventes → 20 €/vendeur.
 * @param {number} salesCount - Nombre de ventes du magasin pour la journée
 * @param {Array} tiers - Paliers (défaut: saturdayChallengeTiers)
 * @returns {number} Bonus en euros (0 si aucun palier atteint)
 */
export const getBonusPerSeller = (salesCount, tiers = saturdayChallengeTiers) => {
  const reached = getReachedTier(salesCount, tiers);
  return reached ? reached.bonusPerSeller : 0;
};

/**
 * Bonus maximum possible (palier le plus haut) pour affichage (ex. "Jusqu'à X €").
 */
export const getMaxBonusPerSeller = (tiers = saturdayChallengeTiers) => {
  const sorted = sortTiersBySalesThreshold(tiers, "desc");
  return sorted.length ? sorted[0].bonusPerSeller : 0;
};
